import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import QuickEntries from "@/pages/home/components/QuickEntries";
import NewsBrief from "@/pages/home/components/NewsBrief";
import StarTicker from "@/pages/home/components/StarTicker";
import SpecialShowcase from "@/pages/home/components/SpecialShowcase";
import SectionHeading from "@/components/feature/SectionHeading";
import ArticleListItem from "@/components/feature/ArticleListItem";
import { usePageMeta } from "@/hooks/usePageMeta";
import { featuredList } from "@/mocks/featured";
import { articleList } from "@/mocks/articles";
import { mediaList } from "@/mocks/media";
import { specialList } from "@/mocks/specials";

export default function Home() {
  usePageMeta(
    "蜜桃传媒 - 专业影视内容与最新传媒资讯平台",
    "蜜桃传媒为您提供丰富的影视内容与传媒行业最新资讯，涵盖电影、电视剧、明星动态及热点事件，助您全面了解娱乐传媒世界。"
  );

  const newsArticles = articleList
    .filter((item) => item.section === "news")
    .sort((a, b) => (a.date < b.date ? 1 : -1));
  const starArticles = articleList
    .filter((item) => item.section === "star")
    .sort((a, b) => (a.date < b.date ? 1 : -1));
  const hotMedia = [...mediaList].sort((a, b) => b.rating - a.rating).slice(0, 5);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <section className="animate-fade-up">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <HeroCarousel items={featuredList} />
          </div>
          <NewsBrief items={newsArticles.slice(0, 6)} />
        </div>
      </section>

      <div className="mt-12 md:mt-16">
        <QuickEntries />
      </div>

      <section className="mt-12 md:mt-16" aria-label="最新影视资讯">
        <SectionHeading
          title="最新影视资讯"
          subtitle="追踪行业动态、剧集热播与产业变化"
          icon="ri-newspaper-line"
          moreHref="/category/movie"
          moreLabel="浏览更多"
        />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="flex flex-col gap-3 lg:col-span-2">
            {newsArticles.slice(0, 8).map((item) => (
              <ArticleListItem key={item.id} item={item} />
            ))}
          </div>

          <aside className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-fire-line text-primary-600"></i>
              高分热榜
            </h3>
            <ul className="flex flex-col gap-3">
              {hotMedia.map((item, index) => (
                <li key={item.id}>
                  <Link to={`/detail/${item.id}`} className="group flex items-center gap-3">
                    <span className="w-5 shrink-0 text-center font-heading text-lg font-black text-primary-500">
                      {index + 1}
                    </span>
                    <div className="relative h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={item.cover}
                        alt={`${item.title}封面`}
                        title={`${item.title}高分推荐`}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="line-clamp-1 text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                        {item.title}
                      </h4>
                      <p className="mt-1 text-xs text-foreground-500">
                        {item.year} · {item.region}
                      </p>
                      <span className="text-xs font-bold text-accent-600">
                        {item.rating.toFixed(1)} 分
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/category/tv"
              className="mt-5 flex w-full items-center justify-center gap-1 whitespace-nowrap rounded-md bg-secondary-200 py-2.5 text-sm font-bold text-secondary-900 transition-colors hover:bg-secondary-300"
            >
              查看电视剧榜单
              <i className="ri-arrow-right-line"></i>
            </Link>
          </aside>
        </div>
      </section>

      <div className="mt-12 md:mt-16">
        <StarTicker items={starArticles} />
      </div>

      <div className="mt-12 md:mt-16">
        <SpecialShowcase items={specialList} />
      </div>

      <section className="mt-12 rounded-lg bg-secondary-100 p-6 md:mt-16 md:p-10" aria-label="网站简介">
        <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
              关于蜜桃传媒
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
              蜜桃传媒定位为专业的影视内容与传媒资讯平台，面向喜爱电影、电视剧与娱乐新闻的广大影视爱好者。我们希望以清晰的分类、可靠的内容与舒适的阅读体验，帮助每一位用户更高效地了解娱乐传媒世界。
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                了解我们
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/search"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-background-50 px-4 py-2.5 text-sm font-bold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-search-line"></i>
                搜索内容
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 lg:grid-cols-1 lg:gap-4">
            <div className="rounded-lg bg-background-50 p-3 text-center lg:flex lg:items-center lg:gap-3 lg:text-left">
              <span className="font-heading text-xl font-black text-primary-600 lg:text-2xl">
                {mediaList.length}+
              </span>
              <p className="text-xs text-foreground-600">影视作品</p>
            </div>
            <div className="rounded-lg bg-background-50 p-3 text-center lg:flex lg:items-center lg:gap-3 lg:text-left">
              <span className="font-heading text-xl font-black text-primary-600 lg:text-2xl">
                {articleList.length}+
              </span>
              <p className="text-xs text-foreground-600">资讯与动态</p>
            </div>
            <div className="rounded-lg bg-background-50 p-3 text-center lg:flex lg:items-center lg:gap-3 lg:text-left">
              <span className="font-heading text-xl font-black text-primary-600 lg:text-2xl">
                {specialList.length}
              </span>
              <p className="text-xs text-foreground-600">精选专题</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}